import { useEffect, useState } from 'react';
import { BookOpenIcon, ChevronRightIcon, GraduationCapIcon } from 'lucide-react';
import type { CourseAudience, ProgressStatus } from '@kennisbank/shared';
import { apiGet } from '../../api/client.js';
import { ELEARNING_BASE } from '../../pages/ELearnings.js';
import type { CourseListItem } from '../../pages/ELearnings.js';

// ─── Gedeelde presentatie-helpers (hergebruikt door de cursusdetail) ─────────

/**
 * Doelgroep-chip in de stijl van de Ring-chips op /documenten. `iedereen`
 * krijgt geen chip (ruis), interne en hoofdkantoor-cursussen wél zodat de
 * gebruiker ziet dat de cursus niet breed zichtbaar is.
 */
export function AudienceChip({ audience }: { audience: CourseAudience }): JSX.Element | null {
  if (audience === 'intern') {
    return (
      <span className="inline-flex items-center whitespace-nowrap rounded-md border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-800">
        SUPPOHRT Intern
      </span>
    );
  }
  if (audience === 'hoofdkantoor') {
    return (
      <span className="inline-flex items-center whitespace-nowrap rounded-md border border-slate-400 bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-900">
        SUPPOHRT Kantoor
      </span>
    );
  }
  return null;
}

const STATUS_CONFIG: Record<ProgressStatus, { label: string; className: string }> = {
  bezig: { label: 'Bezig', className: 'border-brand-200 bg-brand-50 text-brand-700' },
  examenklaar: {
    label: 'Examen gereed',
    className: 'border-mosterdgeel-100 bg-mosterdgeel-10 text-mosterdgeel-600',
  },
  geslaagd: { label: 'Afgerond', className: 'border-green-200 bg-green-50 text-green-700' },
  gezakt: { label: 'Niet geslaagd', className: 'border-red-200 bg-red-50 text-red-600' },
};

export function ProgressStatusBadge({ status }: { status: ProgressStatus }): JSX.Element {
  const cfg = STATUS_CONFIG[status];
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-md border px-2 py-0.5 text-[11px] font-semibold ${cfg.className}`}
    >
      {cfg.label}
    </span>
  );
}

export function ProgressBar({ percentage }: { percentage: number }): JSX.Element {
  const clamped = Math.max(0, Math.min(100, percentage));
  return (
    <div className="h-2.5 w-full overflow-hidden rounded-full bg-brand-100">
      <div
        className="h-full rounded-full bg-brand-500 transition-all duration-500"
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

// ─── Kaart ────────────────────────────────────────────────────────────────────

function CourseCard({
  course,
  onOpen,
}: {
  course: CourseListItem;
  onOpen: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex flex-col gap-3 rounded-2xl border border-brand-100 bg-white p-5 text-left shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2.5">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
            <BookOpenIcon size={18} />
          </span>
          <div className="min-w-0">
            <h2 className="line-clamp-2 text-[15px] font-bold leading-snug text-ink-900 transition-colors group-hover:text-brand-700">
              {course.name}
            </h2>
            <div className="mt-1 flex items-center gap-1.5">
              <AudienceChip audience={course.audience} />
              <ProgressStatusBadge status={course.status} />
            </div>
          </div>
        </div>
        <ChevronRightIcon
          size={18}
          className="mt-1 shrink-0 text-ink-900/25 transition-transform group-hover:translate-x-0.5 group-hover:text-brand-600"
        />
      </div>

      {course.description && (
        <p className="line-clamp-2 text-[12px] leading-relaxed text-ink-900/55">
          {course.description}
        </p>
      )}

      <div className="mt-auto">
        <div className="mb-1.5 flex items-center justify-between text-[11px] text-ink-900/55">
          <span>
            {course.videoQuestionsTotal > 0
              ? `${course.videoQuestionsAnswered} / ${course.videoQuestionsTotal} vragen`
              : `${course.completedLessons} / ${course.totalLessons} lessen`}
          </span>
          <span className="font-medium text-ink-900/70">{course.progressPercentage}%</span>
        </div>
        <ProgressBar percentage={course.progressPercentage} />
      </div>

      {course.hasExam && (
        <p className="flex items-center gap-1.5 text-[11px] font-medium text-ink-900/45">
          <GraduationCapIcon size={13} />
          Met examen
        </p>
      )}
    </button>
  );
}

// ─── Overzicht ────────────────────────────────────────────────────────────────

export function StudentCourseOverview({
  onOpenCourse,
}: {
  onOpenCourse: (courseId: string, directLessonId: string | null) => void;
}): JSX.Element {
  const [courses, setCourses] = useState<CourseListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    void apiGet<{ courses: CourseListItem[] }>(`${ELEARNING_BASE}/courses`)
      .then((d) => setCourses(d.courses))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-[17px] font-bold text-ink-900">
          <BookOpenIcon size={18} className="text-brand-600" />
          E-learnings van SUPPOHRT
        </h2>
        <span className="text-[12px] text-ink-900/50">
          {courses.length > 0 ? `${courses.length} cursussen · direct te starten` : ''}
        </span>
      </div>

      {loading ? (
        <div className="py-16 text-center text-[13px] text-ink-900/40">Laden…</div>
      ) : loadError ? (
        <div className="rounded-2xl border border-dashed border-brand-200 py-16 text-center">
          <p className="text-[14px] font-medium text-error">
            Kon de cursussen niet laden. Ververs de pagina om het opnieuw te proberen.
          </p>
        </div>
      ) : courses.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-brand-200 py-16 text-center">
          <BookOpenIcon size={28} className="mx-auto mb-3 text-ink-900/20" />
          <p className="text-[14px] font-medium text-ink-900/40">
            Er zijn nog geen cursussen voor je beschikbaar.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {courses.map((course) => (
            <CourseCard
              key={course.id}
              course={course}
              onOpen={() => onOpenCourse(course.id, course.directLessonId)}
            />
          ))}
        </div>
      )}
    </section>
  );
}
